import { useState } from "react";
import { Sparkles, X, ArrowRight, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll";
import { APP_VERSION } from "@/lib/constants";

const highlights = [
  {
    title: "Claude Writes the ADO Item",
    desc: "SpecTree Toolbox ADO Formulator",
    detail: "Once the interview is complete, Claude writes the final Bug or User Story text from the confirmed answers only: never a requirement, number or screen nobody gave or confirmed. Title and severity still come from the answers.",
    hue: "from-emerald-500/[0.04] to-transparent",
  },
  {
    title: "Edit Text or Redraft",
    desc: "Review before anything reaches ADO",
    detail: "Edit text opens the draft as plain text boxes, lists one entry per line; SpecTree lays it out in ADO. Redraft tells Claude what to change, under the same no-new-facts rule. A draft whose answers changed is never written.",
    hue: "from-sky-500/[0.04] to-transparent",
  },
  {
    title: "Improve Adds, Never Repeats",
    desc: "Existing item text kept verbatim",
    detail: "When improving an existing item, Claude's draft carries only what the interview added, so the item's own text is kept as it is and never written twice.",
    hue: "from-purple-500/[0.04] to-transparent",
  },
  {
    title: "Findings Saved for Grooming",
    desc: "earlierFindings on ado_get_work_item",
    detail: "What the interview found in the code, with its files and product areas, is saved against the ADO item. /spectree:ado-groom and Toolbox Grooming check those first against the current code.",
    hue: "from-cyan-500/[0.04] to-transparent",
  },
  {
    title: "Needs the Next Toolbox",
    desc: "Nothing changes until then",
    detail: "This release adds the SpecTree side (Toolbox formulator contract 3). The Toolbox that runs Claude's draft and redraft turns ships next; until then the Formulator builds the item from the answers, as before.",
    hue: "from-rose-500/[0.04] to-transparent",
  },
];

export function WhatsNewCard() {
  const navigate = useNavigate();
  const [showPanel, setShowPanel] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  useLockBodyScroll(showPanel);

  return (
    <>
      {/* Dashboard card — Fey style with glow */}
      <div
        className="relative group cursor-pointer rounded-2xl overflow-hidden"
        onClick={() => { setShowPanel(true); }}
      >
        {/* Animated gradient glow underlay */}
        <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-emerald-500/30 via-cyan-500/20 to-purple-500/30 opacity-50 group-hover:opacity-80 transition-opacity duration-500 blur-[1px]" />
        <div className="absolute -inset-[1px] rounded-2xl animate-gradient-shift bg-[length:200%_200%] bg-gradient-to-br from-emerald-500/15 via-transparent to-purple-500/15" />

        <div className="relative glass-card rounded-2xl p-5 border-0 space-y-3 hover:bg-white/[0.04] transition-colors">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/[0.08] rounded-full px-3 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-foreground" />
            <span className="text-xs font-medium text-foreground">What&apos;s New</span>
          </div>

          {/* Summary text */}
          <p className="text-[15px] leading-relaxed text-foreground/90">
            SpecTree {APP_VERSION} lets Claude write the ADO item in the SpecTree Toolbox ADO Formulator,
            with Edit text and Redraft before anything is written, and saves what the interview found for
            grooming (with the next Toolbox release).
          </p>

          {/* Source label */}
          <p className="text-xs text-muted-foreground">
            SpecTree · {APP_VERSION}
          </p>
        </div>
      </div>

      {/* Right overlay panel */}
      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="fixed top-4 right-4 bottom-4 w-[calc(100vw-2rem)] sm:w-[420px] z-50 glass-card rounded-2xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-foreground" />
                <h2 className="text-sm font-semibold">What&apos;s New — {APP_VERSION}</h2>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setShowPanel(false); }}
                className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-white/[0.06] transition-colors"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {highlights.map((h, i) => {
                const isOpen = expandedIndex === i;
                return (
                  <motion.div
                    key={h.title}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06, duration: 0.2 }}
                    className="glass-card rounded-lg overflow-hidden cursor-pointer relative"
                    onClick={() => { setExpandedIndex(isOpen ? null : i); }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-r ${h.hue} pointer-events-none rounded-lg`} />
                    <div className="flex items-center justify-between p-4">
                      <div>
                        <h3 className="text-sm font-medium">{h.title}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{h.desc}</p>
                      </div>
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      </motion.div>
                    </div>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 border-t border-white/[0.04] pt-3">
                            <p className="text-xs text-muted-foreground leading-relaxed">{h.detail}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-white/[0.06]">
              <button
                onClick={() => { setShowPanel(false); void navigate("/whats-new"); }}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                View full changelog <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => { setShowPanel(false); }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
